import { useState, useSyncExternalStore, useEffect, useRef, useCallback } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { useAppStore } from "@/store";
import { useLocation } from "wouter";
import { ArrowRight, Sparkles, Briefcase, GraduationCap, Feather, Share2, Heart, ShoppingCart, Check, Zap, Target, PenTool } from "lucide-react";
import { ScribPostLogo } from "@/components/scribpost-logo";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { useTrainVoice } from "@workspace/api-client-react";
import { useToast } from "@/hooks/use-toast";
import { apiFetch } from "@/lib/api-fetch";
import { USE_CASE_DEFAULT_FORMATS } from "@/lib/studio-presets";
import { useInlineVoiceInput } from "@/hooks/use-inline-voice-input";
import { VoiceInputButton, InlineRecordingIndicator } from "@/components/voice-input-button";

function useStoreHydrated() {
  return useSyncExternalStore(
    useAppStore.persist.onFinishHydration,
    () => useAppStore.persist.hasHydrated(),
    () => false
  );
}

const SUGGESTION_CHIPS = [
  "Paste a long text you wrote",
  "Paste an old email",
  "Paste a tweet thread",
  "Ramble about anything",
];

const USE_CASES = [
  {
    id: "work",
    icon: Briefcase,
    label: "Work",
    description: "Emails, Slack, reports, pitches",
    color: "hsl(205 88% 52%)",
    bg: "hsl(205 88% 52% / 0.1)",
  },
  {
    id: "commerce",
    icon: ShoppingCart,
    label: "Selling & Commerce",
    description: "eBay, Etsy, Marketplace, product listings",
    color: "hsl(38 92% 50%)",
    bg: "hsl(38 92% 50% / 0.1)",
  },
  {
    id: "social",
    icon: Share2,
    label: "Social Media",
    description: "Posts, threads, captions, TikTok",
    color: "hsl(188 90% 42%)",
    bg: "hsl(188 90% 42% / 0.1)",
  },
  {
    id: "creative",
    icon: Feather,
    label: "Creative",
    description: "Stories, scripts, newsletters, blogs",
    color: "hsl(172 70% 40%)",
    bg: "hsl(172 70% 40% / 0.1)",
  },
  {
    id: "school",
    icon: GraduationCap,
    label: "School",
    description: "Essays, notes, cover letters",
    color: "hsl(265 75% 62%)",
    bg: "hsl(265 75% 62% / 0.1)",
  },
  {
    id: "personal",
    icon: Heart,
    label: "Personal",
    description: "Journals, messages, speeches",
    color: "hsl(340 75% 55%)",
    bg: "hsl(340 75% 55% / 0.1)",
  },
];

function countWords(text: string): number {
  return text.trim().split(/\s+/).filter(Boolean).length;
}

function getEncouragementLine(wordCount: number): { text: string; highlight: boolean } {
  if (wordCount >= 100) return { text: "Excellent — your AI voice is going to be very accurate", highlight: true };
  if (wordCount >= 40)  return { text: "That's enough to get started!", highlight: true };
  if (wordCount >= 10)  return { text: "Keep going — more words = sharper voice", highlight: false };
  return { text: "Paste anything — a text, an old email, a tweet thread", highlight: false };
}

export function OnboardingModal() {
  const { hasCompletedOnboarding, completeOnboarding, setDisplayName, sessionId, authToken, isOnboarding } = useAppStore();
  const [name, setName] = useState("");
  const [step, setStep] = useState(0);
  const [selectedUseCases, setSelectedUseCases] = useState<Set<string>>(new Set());
  const [selectedSubPersonas] = useState<Set<string>>(new Set());
  const [, setLocation] = useLocation();
  const dialogRef = useRef<HTMLDivElement>(null);
  const triggerRef = useRef<HTMLElement | null>(null);
  const { toast } = useToast();

  const [trainingSample, setTrainingSample] = useState("");
  const [trainingSubmitted, setTrainingSubmitted] = useState(false);

  const baseUrl = import.meta.env.BASE_URL.replace(/\/$/, "");
  const transcribeAudioInline = useCallback(async (audioBase64: string) => {
    const res = await apiFetch(`${baseUrl}/api/voice/transcribe`, {
      method: "POST",
      headers: { "Content-Type": "application/json", "x-session-id": sessionId },
      body: JSON.stringify({ audioBase64, sessionId }),
    });
    if (!res.ok) {
      const body = await res.json().catch(() => ({})) as Record<string, unknown>;
      throw new Error((body.details as string) || (body.error as string) || "Transcription failed");
    }
    return res.json() as Promise<{ text: string }>;
  }, [baseUrl, sessionId]);

  const onboardingVoiceInput = useInlineVoiceInput({
    onTranscript: (text) => setTrainingSample((prev) => prev ? prev + "\n\n" + text : text),
    transcribeAudio: transcribeAudioInline,
  });

  const hydrated = useStoreHydrated();
  const isOpen = hydrated && !hasCompletedOnboarding && (!!authToken || isOnboarding);

  const trainMutation = useTrainVoice({
    mutation: {
      onSuccess: () => {
        setTrainingSubmitted(true);
        toast({ title: "Voice sample saved!", description: "You're all set — let's start creating." });
      },
      onError: (err: any) => {
        toast({ title: "Couldn't save sample", description: err?.message || "Try again or skip for now.", variant: "destructive" });
      },
    },
    request: { headers: { "x-session-id": sessionId } },
  });

  useEffect(() => {
    if (isOpen) {
      triggerRef.current = document.activeElement as HTMLElement;
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "";
      if (triggerRef.current) {
        triggerRef.current.focus();
        triggerRef.current = null;
      }
    }
    return () => {
      document.body.style.overflow = "";
    };
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen) return;
    const dialog = dialogRef.current;
    if (!dialog) return;
    const focusableSelector = 'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])';
    requestAnimationFrame(() => {
      const focusable = Array.from(dialog.querySelectorAll<HTMLElement>(focusableSelector)).filter(el => !el.hasAttribute("disabled"));
      if (focusable.length > 0) focusable[0].focus();
    });
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key !== "Tab") return;
      const focusable = Array.from(dialog.querySelectorAll<HTMLElement>(focusableSelector)).filter(el => !el.hasAttribute("disabled"));
      if (focusable.length === 0) return;
      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      if (e.shiftKey && document.activeElement === first) {
        e.preventDefault();
        last.focus();
      } else if (!e.shiftKey && document.activeElement === last) {
        e.preventDefault();
        first.focus();
      }
    };
    document.addEventListener("keydown", handleKeyDown);
    return () => document.removeEventListener("keydown", handleKeyDown);
  }, [isOpen]);

  if (!isOpen) return null;

  const handleComplete = () => {
    setDisplayName(name.trim());
    completeOnboarding();
    setLocation(trainingSubmitted ? "/studio" : "/training");
  };

  const handleSaveAndGo = () => {
    if (wordCount >= 40 && !trainingSubmitted) {
      trainMutation.mutate(
        { data: { samples: [trainingSample.trim()], sessionId } },
        {
          onSuccess: () => handleComplete(),
          onSettled: (_data: unknown, err: unknown) => { if (err) handleComplete(); },
        },
      );
    } else {
      handleComplete();
    }
  };

  const toggleUseCase = (id: string) => {
    setSelectedUseCases(prev => {
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      return next;
    });
  };

  const handleStep1Continue = async () => {
    const useCaseContexts = [...selectedUseCases];
    const subUseCaseContexts = [...selectedSubPersonas];

    if (useCaseContexts.length > 0) {
      try {
        await apiFetch(`${baseUrl}/api/voice/preferences`, {
          method: "PUT",
          headers: { "Content-Type": "application/json", "x-session-id": sessionId },
          body: JSON.stringify({ preferences: { useCaseContexts, subUseCaseContexts } }),
        });
      } catch {
        toast({ title: "Couldn't save your focus", description: "Your selection will still work this session.", variant: "destructive" });
      }

      const defaults = useCaseContexts.flatMap((uc) => USE_CASE_DEFAULT_FORMATS[uc] ?? []);
      const deduped = [...new Set(defaults)].slice(0, 3);
      try {
        localStorage.setItem("studio_pinned_presets", JSON.stringify(deduped));
      } catch { }
    }

    setStep(2);
  };

  const wordCount = countWords(trainingSample);
  const encouragement = getEncouragementLine(wordCount);
  const TOTAL_STEPS = 3;

  return (
    <div
      className="fixed inset-0 z-[100] bg-black/80 backdrop-blur-sm flex flex-col items-end justify-end md:items-center md:justify-center md:p-4"
      style={{ paddingBottom: "env(safe-area-inset-bottom)" }}
    >
      <motion.div
        ref={dialogRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby="onboarding-title"
        initial={{ opacity: 0, y: 60 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ type: "spring", stiffness: 280, damping: 28 }}
        className="w-full md:max-w-lg bg-card border-t border-border md:border md:rounded-2xl rounded-t-3xl shadow-2xl shadow-primary/10 overflow-hidden flex flex-col max-h-[88svh] md:max-h-[82vh]"
      >
        {/* Drag handle (mobile only) */}
        <div className="shrink-0 w-10 h-1 bg-muted-foreground/30 rounded-full mx-auto mt-3 mb-1 md:hidden" />

        {/* Animated step content */}
        <AnimatePresence mode="wait">

          {/* ── Step 0: Welcome ── */}
          {step === 0 && (
            <motion.div
              key="welcome"
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -20 }}
              className="flex flex-col min-h-0 flex-1"
            >
              <div className="overflow-y-auto flex-1 px-6 pt-5 pb-2 space-y-5 md:space-y-6 md:px-8">
                <ScribPostLogo size="lg" />

                <div className="space-y-2">
                  <h3 id="onboarding-title" className="text-xl md:text-2xl font-display font-bold leading-snug">
                    Your writing.{" "}
                    <span className="text-transparent bg-clip-text bg-gradient-to-r from-primary to-accent">
                      Amplified.
                    </span>
                  </h3>
                  <p className="text-sm md:text-base text-muted-foreground">
                    ScribPost trains on how you write, then generates content that sounds exactly like you — posts, emails, pitches, anything.
                  </p>
                </div>

                <div className="flex flex-wrap gap-2">
                  <span className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full border border-primary/25 bg-primary/8 text-xs font-medium text-primary">
                    <Zap className="w-3 h-3" /> Ready in 60 seconds
                  </span>
                  <span className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full border border-primary/25 bg-primary/8 text-xs font-medium text-primary">
                    <Target className="w-3 h-3" /> Sounds exactly like you
                  </span>
                  <span className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full border border-primary/25 bg-primary/8 text-xs font-medium text-primary">
                    <PenTool className="w-3 h-3" /> Every format, any occasion
                  </span>
                </div>

                <div className="space-y-2">
                  <label className="text-sm font-medium text-foreground" htmlFor="onboarding-name">
                    What should we call you?
                  </label>
                  <Input
                    id="onboarding-name"
                    placeholder="Your first name"
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === "Enter" && name.trim()) setStep(1);
                    }}
                    className="h-12 text-base rounded-xl bg-background border-border"
                    autoFocus
                  />
                </div>
              </div>

              <div className="shrink-0 px-6 pt-2 space-y-3 md:px-8" style={{ paddingBottom: "calc(1.5rem + env(safe-area-inset-bottom))" }}>
                <Button
                  size="lg"
                  className="w-full h-12 rounded-xl font-bold bg-gradient-to-r from-primary to-accent hover:opacity-90"
                  disabled={!name.trim()}
                  onClick={() => setStep(1)}
                >
                  Let's go
                  <ArrowRight className="w-4 h-4 ml-2" />
                </Button>

                <p className="text-center text-sm text-muted-foreground">
                  Already have an account?{" "}
                  <button
                    type="button"
                    className="text-primary underline underline-offset-2 hover:opacity-80 font-medium"
                    onClick={() => {
                      completeOnboarding();
                      setLocation("/login");
                    }}
                  >
                    Log in
                  </button>
                </p>
              </div>
            </motion.div>
          )}

          {/* ── Step 1: Use case ── */}
          {step === 1 && (
            <motion.div
              key="use-case"
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -20 }}
              className="flex flex-col min-h-0 flex-1"
            >
              <div className="overflow-y-auto flex-1 px-6 pt-5 pb-2 space-y-4 md:px-8">
                <div className="space-y-1">
                  <p className="text-sm font-medium text-primary">We'll set up your tools instantly</p>
                  <h3 id="onboarding-title" className="text-lg md:text-xl font-display font-bold">
                    What do you write most?
                  </h3>
                  <p className="text-sm text-muted-foreground">Pick one or more — we'll personalise your presets automatically.</p>
                </div>

                <div className="grid grid-cols-2 gap-2">
                  {USE_CASES.map((uc) => {
                    const isSelected = selectedUseCases.has(uc.id);
                    return (
                      <motion.button
                        key={uc.id}
                        data-testid={`use-case-${uc.id}`}
                        onClick={() => toggleUseCase(uc.id)}
                        whileTap={{ scale: 0.97 }}
                        className={`flex items-start gap-2.5 p-3 rounded-xl border text-left transition-all relative ${
                          isSelected
                            ? "border-primary bg-primary/8 shadow-sm"
                            : "border-border bg-secondary/30 hover:border-primary/40 hover:bg-secondary/60"
                        }`}
                      >
                        <div
                          className="w-8 h-8 rounded-lg flex items-center justify-center shrink-0 mt-0.5"
                          style={{ background: uc.bg }}
                        >
                          <uc.icon className="w-4 h-4" style={{ color: uc.color }} />
                        </div>
                        <div className="min-w-0 flex-1">
                          <p className="font-semibold text-xs text-foreground leading-tight">{uc.label}</p>
                          <p className="text-[10px] text-muted-foreground leading-snug mt-0.5">{uc.description}</p>
                        </div>
                        {isSelected && (
                          <div className="absolute top-2 right-2 w-4 h-4 rounded-full bg-primary flex items-center justify-center shrink-0">
                            <Check className="w-2.5 h-2.5 text-primary-foreground" />
                          </div>
                        )}
                      </motion.button>
                    );
                  })}
                </div>

                <AnimatePresence>
                  {selectedUseCases.size > 0 && (
                    <motion.div
                      key="presets-ready"
                      initial={{ opacity: 0, y: -6, scale: 0.96 }}
                      animate={{ opacity: 1, y: 0, scale: 1 }}
                      exit={{ opacity: 0, y: -6, scale: 0.96 }}
                      transition={{ duration: 0.18 }}
                      className="flex items-center gap-2"
                    >
                      <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-emerald-500/12 border border-emerald-500/25 text-emerald-400 text-xs font-medium">
                        <Check className="w-3 h-3" /> Presets ready
                      </span>
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>

              <div className="shrink-0 px-6 pt-2 flex gap-2 md:px-8" style={{ paddingBottom: "calc(1.5rem + env(safe-area-inset-bottom))" }}>
                <Button
                  variant="outline"
                  size="sm"
                  className="h-9 rounded-xl font-medium text-sm"
                  onClick={() => setStep(0)}
                >
                  Back
                </Button>
                <Button
                  size="sm"
                  className="flex-1 h-9 rounded-xl font-semibold bg-gradient-to-r from-primary to-accent hover:opacity-90"
                  onClick={handleStep1Continue}
                >
                  {selectedUseCases.size > 0 ? "Looks good" : "Skip"}
                  <ArrowRight className="w-3.5 h-3.5 ml-1.5" />
                </Button>
              </div>
            </motion.div>
          )}

          {/* ── Step 2: Voice training ── */}
          {step === 2 && (
            <motion.div
              key="training"
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -20 }}
              className="flex flex-col min-h-0 flex-1"
            >
              <div className="overflow-y-auto flex-1 px-6 pt-5 pb-2 space-y-4 md:px-8">
                <div className="flex items-start gap-3">
                  <div className="w-10 h-10 rounded-xl flex items-center justify-center shrink-0" style={{ background: "linear-gradient(135deg, hsl(188 90% 42% / 0.2), hsl(265 75% 62% / 0.15))" }}>
                    <Sparkles className="w-5 h-5 text-primary" />
                  </div>
                  <div>
                    <p className="text-xs font-semibold text-primary uppercase tracking-widest">The magic step</p>
                    <h3 id="onboarding-title" className="text-lg font-display font-bold mt-0.5">Feed it one writing sample</h3>
                    <p className="text-sm text-muted-foreground mt-0.5">
                      Paste anything you've written — an email, a tweet, a rant. Just 40 words teaches the AI your tone, rhythm, and style.
                    </p>
                  </div>
                </div>

                <div className="flex flex-wrap gap-2">
                  {SUGGESTION_CHIPS.map((chip) => (
                    <button
                      key={chip}
                      type="button"
                      onClick={() => setTrainingSample(prev => prev ? prev + "\n\n" + chip : chip)}
                      className="px-3 py-1.5 rounded-full border border-border bg-secondary/30 text-xs text-muted-foreground hover:bg-secondary/60 hover:text-foreground transition-colors"
                    >
                      {chip}
                    </button>
                  ))}
                </div>

                <div className="space-y-1.5">
                  <div className="relative">
                    <textarea
                      value={trainingSample}
                      onChange={(e) => setTrainingSample(e.target.value)}
                      placeholder="Paste something you've written here, or tap the mic to record a voice sample..."
                      rows={5}
                      className="w-full rounded-xl border border-border bg-background px-3 py-2.5 pb-10 text-sm resize-none focus:outline-none focus:ring-2 focus:ring-primary/30 placeholder:text-muted-foreground/50"
                    />
                    <div className="absolute bottom-2.5 right-3 flex items-center gap-1.5">
                      <AnimatePresence>
                        {onboardingVoiceInput.isRecording && (
                          <InlineRecordingIndicator
                            recordingTime={onboardingVoiceInput.recordingTime}
                            formatTime={onboardingVoiceInput.formatTime}
                          />
                        )}
                      </AnimatePresence>
                      <VoiceInputButton
                        voiceInput={onboardingVoiceInput}
                        onTranscript={(text) => setTrainingSample((prev) => prev ? prev + "\n\n" + text : text)}
                        showLabel
                        label="Record a voice note"
                      />
                    </div>
                  </div>

                  <div className="flex items-center justify-between min-h-[18px]">
                    <p className={`text-[10px] transition-colors duration-300 ${encouragement.highlight ? "text-primary font-medium" : "text-muted-foreground/60"}`}>
                      {wordCount > 0 ? (encouragement.highlight ? "✓ " : "") + encouragement.text : encouragement.text}
                    </p>
                    {trainingSubmitted && (
                      <p className="text-[10px] text-primary font-medium">✓ Sample saved!</p>
                    )}
                  </div>
                </div>
              </div>

              <div className="shrink-0 px-6 pt-2 flex gap-2 md:px-8" style={{ paddingBottom: "calc(1.5rem + env(safe-area-inset-bottom))" }}>
                <button
                  type="button"
                  onClick={() => setStep(1)}
                  className="text-sm text-muted-foreground hover:text-foreground transition-colors px-2"
                >
                  Back
                </button>
                <Button
                  size="sm"
                  className="flex-1 h-9 rounded-xl font-semibold bg-gradient-to-r from-primary to-accent hover:opacity-90"
                  onClick={handleSaveAndGo}
                  disabled={trainMutation.isPending}
                >
                  {trainMutation.isPending
                    ? "Saving..."
                    : wordCount >= 40 && !trainingSubmitted
                    ? "Save & start writing"
                    : "Skip for now"}
                  <ArrowRight className="w-3.5 h-3.5 ml-1.5" />
                </Button>
              </div>
            </motion.div>
          )}
        </AnimatePresence>

        {/* Step indicator */}
        <div className="shrink-0 flex items-center justify-center gap-1.5 pb-3 pt-1">
          {Array.from({ length: TOTAL_STEPS }).map((_, i) => (
            <div
              key={i}
              className={`rounded-full transition-all duration-300 ${
                i === step
                  ? "w-5 h-1.5 bg-primary"
                  : i < step
                  ? "w-1.5 h-1.5 bg-primary/40"
                  : "w-1.5 h-1.5 bg-muted-foreground/20"
              }`}
            />
          ))}
        </div>
      </motion.div>
    </div>
  );
}
